import { z } from "zod";

// Growth engine (docs/research/GROWTH-ALGORITHMS.md, master plan section 5.11). Vocabularies, French labels, settings
// and the DTOs returned by /api/growth. Every score carries its factors and its data sufficiency so the UI never shows
// a prior as if it were a measurement.

// ---------------------------------------------------------------------------------------------------------------
// Vocabularies

/** COD-aware RFM segments, in first-match order (research section 4.1). Codes match the customers page labels. */
export const RFM_SEGMENTS = ["refus", "champions", "loyal", "cant_lose", "promising", "new", "need_attention", "at_risk", "hibernating", "lost"] as const;
export type RfmSegment = (typeof RFM_SEGMENTS)[number];

export const RFM_SEGMENT_LABELS: Record<RfmSegment, string> = {
  refus: "Refus",
  champions: "Champions",
  loyal: "Fidèles",
  cant_lose: "À ne pas perdre",
  promising: "Prometteurs",
  new: "Nouveaux",
  need_attention: "À réactiver",
  at_risk: "À risque",
  hibernating: "Hibernants",
  lost: "Perdus",
};

/** Default action per segment (shown next to the counts on Relances, Segments tab). */
export const RFM_SEGMENT_ACTIONS: Record<RfmSegment, string> = {
  refus: "Aucune promotion, acompte demandé à la prochaine commande, exclus du retargeting",
  champions: "Traitement VIP, accès en avant-première, demande de parrainage, pas de remise nécessaire",
  loyal: "Demande d'avis et de parrainage, montée en gamme, offres groupées",
  cant_lose: "Contact personnel par un conseiller, meilleure offre",
  promising: "Relance de fidélité et produits complémentaires",
  new: "Demande d'avis puis produits complémentaires, offre de bienvenue pour la deuxième commande",
  need_attention: "Rappel de réapprovisionnement ou produit complémentaire, petite incitation",
  at_risk: "Reconquête avec une offre personnalisée",
  hibernating: "Campagnes peu fréquentes uniquement",
  lost: "Plus de messages payants, sauf deux campagnes par an",
};

/** Delivery failure (RTO) ship gate actions (research section 2.5). */
export const RISK_ACTIONS = ["auto", "standard", "verify", "hold"] as const;
export type RiskAction = (typeof RISK_ACTIONS)[number];

export const RISK_ACTION_LABELS: Record<RiskAction, string> = {
  auto: "Expédition directe",
  standard: "Confirmation standard",
  verify: "Vérification requise",
  hold: "À bloquer",
};

export const RISK_ACTION_DESCRIPTIONS: Record<RiskAction, string> = {
  auto: "Risque faible : la confirmation par bouton suffit et l'expédition automatique est permise.",
  standard: "Risque modéré : confirmation habituelle (assistant ou appel) avant l'expédition.",
  verify: "Risque élevé : appel de vérification même après une confirmation par bouton, adresse complète exigée.",
  hold: "Ne pas expédier sans acompte, paiement en ligne ou décision explicite du marchand.",
};

export const RISK_LEVELS = ["low", "medium", "high"] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];

export const RISK_LEVEL_LABELS: Record<RiskLevel, string> = { low: "Faible", medium: "Moyen", high: "Élevé" };

/** Data sufficiency of a growth result (research section 0.9). */
export const DATA_SUFFICIENCY = ["L0", "L1", "L2"] as const;
export type DataSufficiency = (typeof DATA_SUFFICIENCY)[number];

export const DATA_SUFFICIENCY_LABELS: Record<DataSufficiency, string> = {
  L0: "Estimation (données insuffisantes)",
  L1: "Mesuré (faible volume)",
  L2: "Mesuré",
};

export const ADDRESS_QUALITY_BANDS = ["ready", "collect", "block"] as const;
export type AddressQualityBand = (typeof ADDRESS_QUALITY_BANDS)[number];

export const ADDRESS_QUALITY_LABELS: Record<AddressQualityBand, string> = {
  ready: "Adresse complète",
  collect: "Adresse à compléter",
  block: "Adresse insuffisante",
};

export const STOCK_RISK_STATUSES = ["stockout", "imminent", "below_safety", "overstock", "ok", "no_data"] as const;
export type StockRiskStatus = (typeof STOCK_RISK_STATUSES)[number];

export const STOCK_RISK_LABELS: Record<StockRiskStatus, string> = {
  stockout: "Rupture",
  imminent: "Rupture imminente",
  below_safety: "Sous le stock de sécurité",
  overstock: "Surstock",
  ok: "Stock suffisant",
  no_data: "Pas encore de ventes",
};

export const CLV_LEVELS = ["none", "h1", "model"] as const;
export type ClvLevel = (typeof CLV_LEVELS)[number];

export const CLV_LEVEL_LABELS: Record<ClvLevel, string> = {
  none: "Pas encore assez d'historique (180 jours requis)",
  h1: "Estimation par marge et fidélité moyennes",
  model: "Modèle BG/NBD et Gamma-Gamma",
};

// ---------------------------------------------------------------------------------------------------------------
// Settings (per client, collection growth_settings)

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Heure invalide (HH:MM)");
const minorOrNull = z.number().int("Montant invalide").min(0, "Montant invalide").nullable();
const share = (max: number) => z.number().min(0, "Valeur invalide").max(max, "Valeur trop élevée");

export const growthSettingsUpdateSchema = z
  .object({
    confirmation: z
      .object({
        autoCancelUnreachable: z.boolean(),
        unreachableAfterHours: z.number().int().min(12, "12 h minimum").max(168, "7 jours maximum"),
        retryMinutes: z.array(z.number().int().min(15, "15 minutes minimum").max(4320, "3 jours maximum")).max(4, "4 rappels maximum"),
      })
      .partial(),
    holdouts: z
      .object({
        riskVerifyShare: share(0.2),
        marketingShare: share(0.2),
      })
      .partial(),
    cadence: z
      .object({
        quietHours: z.object({ start: hhmm, end: hhmm }),
        marketingPer24h: z.number().int().min(1).max(5),
        marketingPer7d: z.number().int().min(1).max(14),
        marketingPer30d: z.number().int().min(1).max(30),
        unreadStreakPause: z.number().int().min(2).max(10),
      })
      .partial(),
    economics: z
      .object({
        packagingMinor: minorOrNull,
        handlingMinor: minorOrNull,
        confirmationCostMinor: minorOrNull,
        messagingCostMinor: minorOrNull,
        paymentFeeMinor: minorOrNull,
        forwardShippingMinor: minorOrNull,
        returnShippingMinor: minorOrNull,
        codFeeMinor: minorOrNull,
        damageRate: share(0.5),
        targetProfitShare: share(0.9),
        adSpendSurcharge: z.number().min(1, "Minimum 1").max(2, "Maximum 2"),
      })
      .partial(),
    upsell: z
      .object({
        minMarginShare: share(0.9),
        maxAddOnShareOfSubtotal: z.number().min(0.1).max(1),
      })
      .partial(),
    inventory: z
      .object({
        leadTimeDays: z.number().int().min(1, "1 jour minimum").max(180, "180 jours maximum"),
        serviceZ: z.number().min(1).max(3),
      })
      .partial(),
  })
  .partial();
export type GrowthSettingsUpdate = z.infer<typeof growthSettingsUpdateSchema>;

export interface GrowthSettingsDto {
  confirmation: { autoCancelUnreachable: boolean; unreachableAfterHours: number; retryMinutes: number[] };
  holdouts: { riskVerifyShare: number; marketingShare: number };
  cadence: { quietHours: { start: string; end: string }; marketingPer24h: number; marketingPer7d: number; marketingPer30d: number; unreadStreakPause: number };
  economics: {
    packagingMinor: number | null;
    handlingMinor: number | null;
    confirmationCostMinor: number | null;
    messagingCostMinor: number | null;
    paymentFeeMinor: number | null;
    forwardShippingMinor: number | null;
    returnShippingMinor: number | null;
    codFeeMinor: number | null;
    damageRate: number;
    targetProfitShare: number;
    adSpendSurcharge: number;
  };
  upsell: { minMarginShare: number; maxAddOnShareOfSubtotal: number };
  inventory: { leadTimeDays: number; serviceZ: number };
  confirmationMode: "bot" | "agent" | "auto";
  lastRecomputeAt: string | null;
  currency: string;
}

// ---------------------------------------------------------------------------------------------------------------
// Queries

export const growthHealthQuery = z.object({
  days: z.coerce.number().int().min(7).max(365).default(30),
});
export type GrowthHealthQuery = z.infer<typeof growthHealthQuery>;

export const growthInventoryQuery = z.object({
  status: z.enum([...STOCK_RISK_STATUSES, "all", "attention"] as const).default("attention"),
});
export type GrowthInventoryQuery = z.infer<typeof growthInventoryQuery>;

export const upsellRequestSchema = z.object({
  items: z
    .array(
      z.object({
        productId: z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide"),
        variantId: z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide").nullable().optional(),
        qty: z.number().int().min(1).max(1000),
      }),
    )
    .min(1, "Ajoutez au moins un produit")
    .max(50),
  customerId: z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide").nullable().optional(),
  riskLevel: z.enum(RISK_LEVELS).nullable().optional(),
  priceObjection: z.boolean().optional().default(false),
});
export type UpsellRequestInput = z.infer<typeof upsellRequestSchema>;

// ---------------------------------------------------------------------------------------------------------------
// DTOs

export interface GrowthFactorDto {
  code: string;
  label: string;
  /** Log-odds contribution (risk, lead score) or points (address quality). Positive raises the score. */
  contribution: number;
  detail?: string;
}

export interface RateEstimateDto {
  /** Raw measured rate, null without data. */
  measured: number | null;
  /** Cold-start prior (decision D13: per merchant data only, research priors otherwise). */
  prior: number;
  /** Rate used by the computations (Beta-binomial shrinkage of the measurement toward the prior). */
  used: number;
  successes: number;
  trials: number;
  sufficiency: DataSufficiency;
  /** Share of the prior in the used value: k / (n + k). */
  priorWeight: number;
}

export interface BreakEvenDto {
  /** Maximum cost per valid placed order (what a platform purchase on submit may cost). */
  cplMinor: number;
  cpaConfirmedMinor: number;
  cpdMinor: number;
  /** Break-even platform ROAS when the platform values orders at submit. */
  roasSubmit: number;
  /** Break-even delivered ROAS. */
  roasDelivered: number;
}

export interface ClientHealthDto {
  currency: string;
  period: { from: string; to: string; days: number };
  funnel: { placed: number; validPlaced: number; confirmed: number; shipped: number; delivered: number; returned: number; inTransit: number; cancelled: number };
  rates: { confirmation: RateEstimateDto; delivery: RateEstimateDto; netDelivery: number };
  revenue: { placedMinor: number; confirmedMinor: number; deliveredMinor: number; expectedDeliveredMinor: number };
  /** Expected delivered value per confirmed order: V x pDeliver (decision D17, "valeur attendue"). */
  expectedDeliveredValue: { perOrderMinor: number | null; pDeliver: number; explanation: string };
  ads: { spendMinor: number | null; known: boolean; note: string };
  profit: {
    costsComplete: boolean;
    missingCosts: string[];
    aovMinor: number | null;
    contributionIfDeliveredMinor: number | null;
    lossIfRtoMinor: number | null;
    expectedPerOrderMinor: number | null;
    breakEven: BreakEvenDto | null;
    targets: BreakEvenDto | null;
    deliveredRoas: number | null;
    mer: number | null;
    poas: number | null;
    contributionAfterMarketingMinor: number | null;
  };
  risk: { scored: number; byAction: Record<RiskAction, number>; holdouts: number };
  confirmation: { mode: "bot" | "agent" | "auto"; awaiting: number; botActive: number; autoCancelled: number; confirmedByBot: number };
  priors: { confirmRate: number; deliveryRate: number; newCustomerRto: number };
  notes: string[];
}

export interface SegmentSummaryDto {
  segment: RfmSegment;
  label: string;
  action: string;
  count: number;
  revenueMinor: number;
  /** Customers with marketing consent and no opt-out (reachable by a marketing template). */
  reachable: number;
  avgClvMinor: number | null;
}

export interface SegmentsSummaryDto {
  currency: string;
  computedAt: string | null;
  totalCustomers: number;
  segmented: number;
  segments: SegmentSummaryDto[];
  ipi: { days: number; source: "measured" | "default"; intervals: number };
  clv: { level: ClvLevel; label: string; avgMinor: number | null };
}

export interface AddressQualityDto {
  score: number;
  band: AddressQualityBand;
  parts: GrowthFactorDto[];
  suggestions: string[];
}

export interface OrderHygieneDto {
  phone: { e164: string | null; valid: boolean; type: string | null };
  addressQuality: AddressQualityDto;
  flags: Array<{ code: string; label: string }>;
  duplicateOf: string[];
}

export interface OrderRiskDto {
  orderId: string;
  number: string;
  currency: string;
  computed: boolean;
  score: number | null;
  p: number | null;
  pStar: number | null;
  level: RiskLevel | null;
  action: RiskAction | null;
  actionLabel: string | null;
  actionDescription: string | null;
  holdout: boolean;
  factors: GrowthFactorDto[];
  hardFlags: string[];
  sufficiency: DataSufficiency;
  sufficiencyLabel: string;
  priorWeight: number;
  algoVersion: string | null;
  computedAt: string | null;
  hygiene: OrderHygieneDto | null;
  economics: { contributionIfDeliveredMinor: number | null; lossIfRtoMinor: number | null; pDeliver: number | null; expectedDeliveredValueMinor: number | null; missingCosts: string[] };
  explanation: string;
}

export interface UpsellSuggestionDto {
  kind: "quantity" | "cross_sell" | "bundle";
  productId: string;
  variantId: string | null;
  name: string;
  imageUrl: string | null;
  qty: number;
  /** Price to pay for the offer (catalog price or merchant quantity offer), minor units. */
  priceMinor: number;
  /** Regular price of the same quantity, minor units. */
  regularPriceMinor: number;
  savingMinor: number;
  reasonCode: "QUANTITY_SAVING" | "FREQUENTLY_BOUGHT_TOGETHER" | "MERCHANT_BUNDLE" | "BEST_SELLER";
  reason: string;
  /** Short French phrase the AI agent may rephrase. Prices always come from priceMinor. */
  copyHint: string;
  marginKnown: boolean;
  lift: number | null;
  confidence: number | null;
}

export interface ProductPairDto {
  productId: string;
  name: string;
  ordersTogether: number;
  lift: number;
  confidence: number;
  bundle: boolean;
}

export interface ProductUpsellDto {
  productId: string;
  currency: string;
  offers: UpsellSuggestionDto[];
  pairs: ProductPairDto[];
  multiItemOrders: number;
  note: string;
}

export interface InventoryRiskDto {
  productId: string;
  name: string;
  sku: string;
  imageUrl: string | null;
  stock: number;
  /** Available to promise: stock minus the expected confirmations of pending orders. */
  atp: number;
  unitsPending: number;
  velocity: number;
  sigmaDaily: number;
  daysOfCover: number | null;
  safetyStock: number;
  reorderPoint: number;
  suggestedQty: number;
  leadTimeDays: number;
  status: StockRiskStatus;
  statusLabel: string;
  /** True when ads promoting the product should be paused (stock exhausted or under one day of demand). */
  pauseAds: boolean;
  lostSalesMinor: number;
  sufficiency: DataSufficiency;
}

export interface InventoryRisksDto {
  currency: string;
  items: InventoryRiskDto[];
  counts: Record<StockRiskStatus, number>;
}
